import dayjs from 'dayjs';
import type { BankTransferStatus } from '../../../types/company';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'suspended';
export type BillingCycle = 'monthly' | 'quarterly' | 'yearly';
export type PaymentProvider = 'stripe' | 'manual' | 'bank_transfer';

export interface SubscriptionContractAddOn {
  addOnCode: string;
  quantity: number;
  unitPrice: number;
}

export interface SubscriptionUsage {
  period: string;
  usageQuantity?: number | null;
  usageCharge?: number | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  currency?: string | null;
}

export interface SubscriptionContractRow {
  contractId: string;
  subscriptionId: string;
  companyId: string;
  companyName: string;
  companyEmail?: string | null;
  planCode: string;
  scheduledPlanCode?: string | null;
  scheduledPlanEffectiveDate?: string | Date | null;
  status: SubscriptionStatus | string;
  currency?: string | null;
  mrr?: number | string | null;
  billingCycle?: BillingCycle | string | null;
  contractStartDate?: string | Date | null;
  contractEndDate?: string | Date | null;
  nextInvoiceDate?: string | Date | null;
  paymentProvider?: PaymentProvider | string | null;
  addOns?: SubscriptionContractAddOn[];
  latestUsage?: SubscriptionUsage | null;
  flags?: string[];
  metadata?: Record<string, unknown> | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  latestInvoiceId?: string | null;
  stripeLatestInvoiceId?: string | null;
  bankTransferStatus?: BankTransferStatus | null;
  bankTransferConfirmedAt?: string | Date | null;
  bankTransferMemo?: string | null;
  updatedAt: string | Date;
  createdAt: string | Date;
}

export interface SubscriptionCompany {
  id: string;
  contractId: string;
  subscriptionId: string;
  companyId: string;
  name: string;
  companyName: string;
  nameKana?: string | null;
  email?: string | null;
  companyEmail?: string | null;
  phone?: string | null;
  subscriptionPlan: string;
  planCode: string;
  scheduledPlanCode?: string | null;
  scheduledPlanEffectiveDate?: string | Date | null;
  subscriptionStatus: SubscriptionStatus;
  status: SubscriptionStatus;
  subscriptionAddonUnits?: number | null;
  userAddonUnits?: number | null;
  currency?: string | null;
  mrr?: number | string | null;
  billingCycle?: BillingCycle | string | null;
  paymentProvider?: PaymentProvider | string | null;
  contractStartDate?: string | Date | null;
  contractEndDate?: string | Date | null;
  subscriptionStartsAt?: string | Date | null;
  subscriptionEndsAt?: string | Date | null;
  subscriptionCanceledAt?: string | Date | null;
  nextInvoiceDate?: string | Date | null;
  addOns?: SubscriptionContractAddOn[];
  latestUsage?: SubscriptionUsage | null;
  flags?: string[];
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripeLatestInvoiceId?: string | null;
  latestInvoiceId?: string | null;
  onboardingDiscountCode?: string | null;
  stripePromotionCodeId?: string | null;
  bankTransferStatus?: BankTransferStatus | null;
  bankTransferConfirmedAt?: string | Date | null;
  bankTransferMemo?: string | null;
  updatedAt: string | Date;
  createdAt: string | Date;
}

export interface DiscountCodeRow {
  subscriptionId: string;
  companyName: string;
  planCode: string;
  status: SubscriptionStatus;
  paymentProvider: PaymentProvider | string;
  latestInvoiceId: string | null;
}

export interface ContractListFilters {
  search?: string;
  status?: string;
  planCode?: string;
  billingCycle?: string;
  paymentProvider?: string;
}

export interface SavedFilterSet {
  id: string;
  name: string;
  createdAt: string;
  filters: ContractListFilters;
}

export const STATUS_LABELS: Record<string, string> = {
  trialing: '無料登録',
  active: '稼働中',
  past_due: '支払遅延',
  canceled: '解約',
  suspended: '停止中',
};

export const DEFAULT_COLUMN_KEYS = [
  'company',
  'plan',
  'status',
  'mrr',
  'billingCycle',
  'nextInvoice',
  'paymentProvider',
  'usage',
  'flags',
  'updatedAt',
] as const;

const numberFormatter = new Intl.NumberFormat('ja-JP');

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) {
    return '¥0';
  }
  const raw = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric)) {
    return '¥0';
  }
  const rounded = Math.round(numeric);
  return `¥${numberFormatter.format(rounded)}`;
}

export function formatDate(value?: string | Date | null, fallback = '-'): string {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.format('YYYY-MM-DD HH:mm');
}

export function formatDateShort(value?: string | Date | null, fallback = '-'): string {
  if (!value) return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  return d.format('YYYY-MM-DD');
}

function escapeCsvValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function toCsv(rows: DiscountCodeRow[]): string {
  const header = [
    'Subscription ID',
    '会社名',
    'プラン',
    'ステータス',
    '支払い方法',
    '最新Invoice ID',
  ];
  const lines = rows.map((row) =>
    [
      row.subscriptionId ?? '',
      row.companyName ?? '',
      row.planCode ?? '',
      row.status ?? '',
      row.paymentProvider ?? '',
      row.latestInvoiceId ?? '',
    ]
      .map((value) => escapeCsvValue(String(value ?? '')))
      .join(','),
  );
  return [header.join(','), ...lines].join('\r\n');
}

export function toContractCsv(rows: SubscriptionContractRow[]): string {
  const header = [
    '契約ID',
    '会社名',
    'プラン',
    'ステータス',
    'MRR (JPY)',
    '課金サイクル',
    '次回請求日',
    '支払いプロバイダー',
    '最新利用量',
    '利用課金額 (JPY)',
  ];

  const lines = rows.map((row) => {
    const usage = row.latestUsage;
    const values = [
      row.contractId ?? '',
      row.companyName ?? '',
      row.planCode ?? '',
      STATUS_LABELS[row.status] ?? (row.status ?? ''),
      formatCurrency(row.mrr ?? 0),
      row.billingCycle ?? '',
      row.nextInvoiceDate ? formatDateShort(row.nextInvoiceDate, '') : '',
      row.paymentProvider ?? '',
      usage?.usageQuantity ?? '',
      usage?.usageCharge !== undefined && usage?.usageCharge !== null ? formatCurrency(usage.usageCharge) : '',
    ];

    return values
      .map((value) => escapeCsvValue(value === null || value === undefined ? '' : String(value)))
      .join(',');
  });

  return [header.join(','), ...lines].join('\r\n\r\n');
}

export function calculateSummary(rows: SubscriptionContractRow[]) {
  const total = rows.length;
  const active = rows.filter((row) => row.status === 'active').length;
  const pastDue = rows.filter((row) => row.status === 'past_due').length;
  const pendingBank = rows.filter((row) => row.bankTransferStatus && row.bankTransferStatus !== 'confirmed').length;
  const flagged = rows.filter((row) => Array.isArray(row.flags) && row.flags.length > 0).length;
  return { total, active, pastDue, pendingBank, flagged };
}
